import React, { useState } from 'react';
import Layout from '../components/Layout';
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  MessageSquare,
  User,
  FileText,
} from 'lucide-react';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Ici vous pouvez ajouter la logique pour envoyer le formulaire
    console.log('Form submitted:', formData);
    alert('Merci de nous avoir contactés! Nous répondrons bientôt.');
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Téléphone',
      content: '+33 7 36 97 43 88',
      link: 'tel:+33736974388',
      color: 'text-green-400',
      bgColor: 'bg-green-400/20',
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'contact@primorixx.com',
      link: 'mailto:contact@primorixx.com',
      color: 'text-blue-400',
      bgColor: 'bg-blue-400/20',
    },
    {
      icon: MapPin,
      title: 'Adresse',
      content: '17 Rue Gay-Lussac, 75005 Paris, France',
      link: 'https://maps.google.com/?q=17+Rue+Gay-Lussac,+75005+Paris,+France',
      color: 'text-red-400',
      bgColor: 'bg-red-400/20',
    },
    {
      icon: Clock,
      title: 'Heures d\'Ouverture',
      content: 'Lundi à Vendredi: 09:00 - 18:00',
      link: null,
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-400/20',
    },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact - Primorixx',
    description:
      'Contactez Primorixx. Nous sommes là pour vous aider avec vos questions sur les paris sportifs en ligne en France.',
    url: 'https://primorixx.com/contact',
    mainEntity: {
      '@type': 'Organization',
      name: 'Primorixx',
      telephone: '+33736974388',
      email: 'contact@primorixx.com',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '17 Rue Gay-Lussac',
        addressLocality: 'Paris',
        postalCode: '75005',
        addressCountry: 'FR',
      },
    },
  };

  return (
    <Layout
      title="Contact | Primorixx"
      description="Contactez Primorixx. Téléphone: +33 7 36 97 43 88. Adresse: 17 Rue Gay-Lussac, 75005 Paris, France."
      jsonLd={jsonLd}
    >
      <div className="min-h-screen bg-dark-bg pt-24 pb-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-dark-surface to-dark-bg">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-dark-accent/20 rounded-2xl mb-6">
                <MessageSquare className="w-8 h-8 text-dark-accent" />
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 gradient-text">
                Contactez-nous
              </h1>
              <p className="text-dark-muted text-lg md:text-xl">
                Avez-vous une question ou une suggestion? Nous sommes là pour vous aider!
              </p>
            </div>
          </div>
        </section>

        {/* Contact Information */}
        <section className="py-20 bg-dark-bg">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              {contactInfo.map((info, index) => (
                <div
                  key={index}
                  className="bg-dark-card border border-dark-border rounded-xl p-6 hover:border-dark-accent transition-all duration-300 text-center"
                >
                  <div
                    className={`inline-flex items-center justify-center w-14 h-14 ${info.bgColor} rounded-xl mb-4`}
                  >
                    <info.icon className={`w-7 h-7 ${info.color}`} />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">
                    {info.title}
                  </h3>
                  {info.link ? (
                    <a
                      href={info.link}
                      target={info.link.startsWith('http') ? '_blank' : '_self'}
                      rel={
                        info.link.startsWith('http')
                          ? 'noopener noreferrer'
                          : undefined
                      }
                      className={`${info.color} hover:opacity-80 transition-opacity break-words`}
                    >
                      {info.content}
                    </a>
                  ) : (
                    <p className="text-dark-muted break-words">
                      {info.content}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form and Map Section */}
        <section className="py-20 bg-dark-surface">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
              {/* Contact Form */}
              <div className="bg-dark-card border border-dark-border rounded-xl p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <Send className="w-6 h-6 text-dark-accent" />
                  <h2 className="text-2xl font-bold text-white">
                    Envoyez-nous un Message
                  </h2>
                </div>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-dark-text mb-2"
                    >
                      <User className="inline w-4 h-4 mr-2" />
                      Nom Complet
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-dark-surface border border-dark-border rounded-lg text-white placeholder-dark-muted focus:outline-none focus:border-dark-accent transition-colors"
                      placeholder="Votre nom"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-dark-text mb-2"
                    >
                      <Mail className="inline w-4 h-4 mr-2" />
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-dark-surface border border-dark-border rounded-lg text-white placeholder-dark-muted focus:outline-none focus:border-dark-accent transition-colors"
                      placeholder="votre@email.com"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="subject"
                      className="block text-sm font-medium text-dark-text mb-2"
                    >
                      <FileText className="inline w-4 h-4 mr-2" />
                      Sujet
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-dark-surface border border-dark-border rounded-lg text-white placeholder-dark-muted focus:outline-none focus:border-dark-accent transition-colors"
                      placeholder="De quoi voulez-vous parler?"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-dark-text mb-2"
                    >
                      <MessageSquare className="inline w-4 h-4 mr-2" />
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 bg-dark-surface border border-dark-border rounded-lg text-white placeholder-dark-muted focus:outline-none focus:border-dark-accent transition-colors resize-none"
                      placeholder="Écrivez votre message ici..."
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full inline-flex items-center justify-center px-6 py-3 bg-dark-accent hover:bg-dark-accentHover text-white rounded-lg font-semibold transition-colors"
                  >
                    Envoyer le Message
                    <Send className="ml-2 w-5 h-5" />
                  </button>
                </form>
              </div>

              {/* Map and Additional Info */}
              <div className="space-y-6">
                {/* Map */}
                <div className="bg-dark-card border border-dark-border rounded-xl overflow-hidden">
                  <div className="aspect-video bg-dark-surface">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2624.1041815445476!2d2.341549667459094!3d48.845492!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e671c2c5bbf05d%3A0x471fd61f91ff1e02!2s17%20Rue%20Gay-Lussac%2C%2075005%20Paris%2C%20France!5e0!3m2!1sfr!2sfr!4v1234567890123!5m2!1sfr!2sfr"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Localisation Primorixx"
                      className="w-full h-full"
                    ></iframe>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-white mb-2">
                      Notre Localisation
                    </h3>
                    <p className="text-dark-muted">
                      17 Rue Gay-Lussac, 75005 Paris, France
                    </p>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                  <h3 className="text-xl font-bold text-white mb-4">
                    Informations Supplémentaires
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-dark-muted text-sm mb-2">
                        <strong className="text-white">Heures d'Ouverture:</strong>
                      </p>
                      <p className="text-dark-text">
                        Lundi à Vendredi: 09:00 - 18:00
                      </p>
                      <p className="text-dark-text">Samedi et Dimanche: Fermé</p>
                    </div>
                    <div>
                      <p className="text-dark-muted text-sm mb-2">
                        <strong className="text-white">Délai de Réponse:</strong>
                      </p>
                      <p className="text-dark-text">
                        Nous répondons à toutes les demandes dans un délai de 24-48 heures
                        ouvrables.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Quick Links */}
        <section className="py-20 bg-dark-bg">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 gradient-text">
                Questions Fréquentes?
              </h2>
              <p className="text-dark-muted text-lg mb-8">
                Avant de nous contacter, consultez notre section FAQ. Il se peut que
                votre question y soit déjà répondue!
              </p>
              <a
                href="/faq"
                className="inline-flex items-center px-6 py-3 bg-dark-surface hover:bg-dark-border text-white rounded-lg font-semibold transition-colors border border-dark-border"
              >
                Voir la FAQ
              </a>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default Contact;

